import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {ApiClientError} from '@/api/client';
import {authApi} from '@/api/auth';
import type {User} from '@/types/api';

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    refetchUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({children}: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // The session is a cookie now, so there is nothing to check before asking:
    // /auth/me answers 401 when the caller is anonymous.
    const fetchUser = useCallback(async () => {
        try {
            const userData = await authApi.me();
            setUser(userData);
        } catch (error) {
            // Only clear on auth errors (401), not on network errors
            if (error instanceof ApiClientError && error.code === 'UNAUTHORIZED') {
                setUser(null);
            }
            // For network errors, keep user state - they might reconnect
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email: string, password: string) => {
        setUser(await authApi.login(email, password));
    };

    const logout = async () => {
        window.location.href = '/logout';
    };

    const refetchUser = async () => {
        await fetchUser();
    };

    const value: AuthContextType = {
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refetchUser
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
